import { redirect } from 'react-router-dom';

// Constants
import { formNaming } from '../../../../../../components/forms/NewCommunicationForm/constants';

// GraphQL
import { client } from '../../../../../../gql/client';
import { SAVE_COMMUNICATION } from '../../../../../../gql/mutations/communication';
import { UPDATE_PARENT_ORDER } from '../../../../../../gql/mutations/orders';
import { GET_ORDER_BY_ID } from '../../../../../../gql/queries/orders/orders';
import { GET_COMMUNICATION_BY_ID } from '../../../../../../gql/queries/communication';

// State
import { indexTabFormReset } from '../../../../../../state/actions/ui/tabs';
import { deselectedRowModalTable } from '../../../../../../state/actions/ui/selectRowTableModal';

// Types
import { SaveDataParams } from './types';

//Utils
import { tabsType } from '../../../../../../state/reducers/ui/tabs';
import { parentFromSelection, resolveEditCommunicationRedirect } from './resolveRedirect';
import { addCompoundOperation } from '../../../../../../offline/offlineCompoundQueue';
import { isOnlineNow } from '../../../../../../offline/connectivity';
import { isActuallyOnline } from '../../../../../../offline/checkConnectivity';
import * as Sentry from '@sentry/react';
import {
  assertDraftOwner,
  DraftOwnerMismatchError,
} from '../../../../../../utils/draftOwnership';
import { COMMUNICATION_EDIT_SCOPE } from '../../../../../../utils/draftScopes';

/**
 * Detección de mutación encolada por OfflineQueueLink (mismo helper que
 * NewOrder/EditAccident/etc.). No es un error real: se replay-ea al reconectar.
 */
function isOfflineQueuedMutationError(err: unknown): boolean {
  const e = err as {
    offlineQueued?: boolean;
    message?: string;
    networkError?: { offlineQueued?: boolean };
  };
  return (
    e?.offlineQueued === true ||
    e?.networkError?.offlineQueued === true ||
    (e?.message ?? '').includes('Mutation queued for offline sync')
  );
}

export const saveData = async ({
  formData,
  store,
  storedDataOTSelected,
  tabSelected,
  params,
}: SaveDataParams) => {
  const dispatch = store.dispatch;

  // Barrera (M3): se guarda la comunicación de la URL (`:id`) o no se guarda
  // nada. Antes el destino era `parentOrderId` = `loaderData.id`, es decir, el
  // de la copia de sesión, y la URL solo decidía el redirect (S2/S3). No debería
  // saltar nunca —el loader descarta la copia ajena y la pantalla remonta—; si
  // salta no se escribe NI se encola nada. Aquí no hay reintento de cola: la
  // cola reproduce `syncCommunication` con el `commInput` ya capturado.
  const urlOrderId = params?.id;
  assertDraftOwner(COMMUNICATION_EDIT_SCOPE, urlOrderId, { isReplay: false });
  const targetOrderId = formData?.get('parentOrderId')?.toString();
  // Falla CERRADO: sin `:id` (la action solo existe bajo
  // `edit-communication/:id/summary`, así que en un guardado vivo siempre
  // llega) tampoco se escribe.
  if (targetOrderId !== urlOrderId) {
    const mismatch = new DraftOwnerMismatchError(
      COMMUNICATION_EDIT_SCOPE.name,
      targetOrderId ?? null,
      urlOrderId ?? '(sin :id)'
    );
    Sentry.captureException(mismatch, {
      tags: { guard: `draft-owner-mismatch:${COMMUNICATION_EDIT_SCOPE.name}` },
    });
    throw mismatch;
  }

  // Destino post-guardado. Se resuelve AQUÍ, antes de tocar nada, porque la
  // resolución lee `COMMUNICATION_NAVIGATION_CONTEXT` y más abajo se borra —
  // y porque la rama offline también lo necesita.
  const redirectTarget = resolveEditCommunicationRedirect(
    formData,
    parentFromSelection(tabSelected, storedDataOTSelected)
  );
  // ¿Acabó la edición en la cola compound en vez de en el servidor?
  let queuedOffline = false;

  if (formData) {
    let indexExpedition: number | undefined;
    let indexType: number | undefined;
    let capitolId: string | undefined;
    let subCapitolId: string | undefined;
    let classificationId: string | undefined;

    const registerInitDate = formData.get('registerInitDate');
    const groupInterId = formData.get('groupInter');
    const mediaId = formData.get('media');
    const sendStatus = formData.get('sendStatus');
    const concessionId = formData.get('concession');
    const roadId = formData.get('road');
    const parentOrderId = formData.get('parentOrderId');
    const trackValue = formData.get('track');
    const direction = formData.get('direction');
    const margin = formData.get('margin');
    const laneValue = formData.get('lane');
    const pkInit = formData.get('pkInit') as string;
    const pkEnd = formData.get('pkEnd') as string;
    const description = formData.get('description');
    const notes = formData.get('notes');
    // T36: Read communication's own predefinido/capitol/subcapitol/classification from form
    capitolId = (formData.get('capitol') as string) || undefined;
    subCapitolId = (formData.get('subCapitol') as string) || undefined;
    classificationId = (formData.get('classification') as string) || undefined;
    const predefinedOrderId = (formData.get('predefine') as string) || undefined;

    const {
      data: { order: parentOrder },
    } = await client.query({
      query: GET_ORDER_BY_ID,
      variables: {
        id: parentOrderId,
      },
    });

    const communicationId = parentOrder.communicationIDs[0];

    // Bloque C-7: UPDATE_ORDER + UPDATE_ORDER_RELATIONSHIPS sobre el Order de la
    // Communication eliminados. SAVE_COMMUNICATION (update path) ya persiste
    // description, notes, registerInitDate, PKs, capitol/subCapitol/classification,
    // predefinedOrder, concession y road sincronizados a Order ↔ Communication
    // dentro de su propia $transaction. Se mantiene UPDATE_PARENT_ORDER porque
    // saveCommunicationUpdate no cambia parent (re-relación).

    if (tabSelected > 0) {
      const {
        data: { order },
      } = await client.query({
        query: GET_ORDER_BY_ID,
        variables: {
          id: storedDataOTSelected?.id,
        },
      });

      // NOTE: el `state` del Order padre NO se copia a la Communication.
      // Communication tiene su propio ciclo de vida (open/annulled/end) independiente
      // del padre (que puede estar en work-in-progress, pending-validation, etc.).
      // Copiarlo producía estados inválidos y desincronización con el Order paralelo
      // de la Communication. Ver FIX_STATE_SYNC_DUAL_ENTITIES.md "Fase 2".
      indexExpedition = order.indexExpedition;
      indexType = order?.indexType;
      // Use communication's own values from form if available, fallback to parent order
      capitolId = capitolId || order.capitolIDs[0];
      subCapitolId = subCapitolId || order.subCapitolIDs[0];
      classificationId = classificationId || order?.classificationIDs?.[0];

      // Bloque C-7.1: solo re-relacionar si el parent realmente cambió.
      // Antes se disparaba ConnectNewParentOrderById en cada save aunque
      // storedDataOTSelected fuera el mismo parent que ya tenía la comm.
      // Eso causaba (1) incremento innecesario de indexType en backend y
      // (2) deadlocks P2034 intermitentes al solaparse el lock de Order
      // con la transacción de SaveCommunication que viene justo después.
      const currentParentId = parentOrder.parentExpeditionOrderIDs?.[0];
      const newParentId = storedDataOTSelected.id;

      if (newParentId && newParentId !== currentParentId) {
        try {
          await client.mutate({
            mutation: UPDATE_PARENT_ORDER,
            variables: {
              input: {
                orderToUpdate: parentOrderId,
                newParentOrderId: newParentId,
              },
            },
          });
        } catch (parentErr) {
          // FIX pérdida de datos offline: si esta mutación quedó ENCOLADA
          // (OfflineQueueLink), NO abortamos — continuamos para que el
          // SAVE_COMMUNICATION de abajo también se ENCOLE. La cola replay-ea
          // ambas en orden al reconectar (antes: solo se encolaba esta, los
          // edits de campos se PERDÍAN y se redirigía con falso éxito).
          // Un error real sí aborta (re-throw) — sin re-colgar a medias.
          if (isOfflineQueuedMutationError(parentErr)) {
            console.log(
              '📴 UPDATE_PARENT_ORDER encolado offline — continuando para encolar también SAVE_COMMUNICATION'
            );
          } else {
            throw parentErr;
          }
        }
      }
    }

    // ── OFFLINE (espejo de EditAccident/saveData y de NewComunication) ──
    // `SaveCommunication` está en `COMPOUND_MANAGED_OPERATIONS` del
    // `OfflineQueueLink`: ese link la deja pasar A PROPÓSITO para que el
    // saveData la encole él mismo en la cola compound. Editar comunicación
    // nunca lo hizo: el fetch salía, moría con "Failed to fetch", el catch
    // solo reconocía `isOfflineQueuedError` (que este link NUNCA marca para
    // estas 5 mutations) y la edición acababa en ErrorPage con los cambios
    // PERDIDOS. Crear comunicación sí lo hacía bien desde C-7.3; era la
    // edición la que faltaba. Reportado por el usuario 2026-08-05.
    // Sondeo real (2026-09-11): con cobertura mala `navigator.onLine` dice
    // `true` y esta edición no entraba por la rama que encola padre + hija.
    const isOffline = isOnlineNow() ? !(await isActuallyOnline()) : true;
    const commInput = {
      id: communicationId,
      capitolId: capitolId ? capitolId : undefined,
      subCapitolId: subCapitolId ? subCapitolId : undefined,
      classification: classificationId ? classificationId : undefined,
      predefinedOrderId: predefinedOrderId ? predefinedOrderId : undefined,
      registerInitDate: registerInitDate ? registerInitDate : undefined,
      sendStatus: sendStatus ? sendStatus : undefined,
      interlocutorGroupId: groupInterId ? groupInterId : undefined,
      mediaOfCommunicationId: mediaId ? mediaId : undefined,
      roadId: roadId ? roadId : undefined,
      concessionId: concessionId ? concessionId : undefined,
      track: trackValue ? trackValue : undefined,
      direction: direction ? direction : undefined,
      margin: margin ? margin : undefined,
      lane: laneValue ? laneValue : undefined,
      pkInitKm: pkInit ? parseInt(pkInit.split('+')[0]) : undefined,
      pkInitMeter: pkInit ? parseInt(pkInit.split('+')[1]) : undefined,
      pkEndKm: pkEnd ? parseInt(pkEnd.split('+')[0]) : undefined,
      pkEndMeter: pkEnd ? parseInt(pkEnd.split('+')[1]) : undefined,
      description: description ? (description as string) : undefined,
      notes: notes ? (notes as string) : undefined,
    };

    if (
      indexExpedition ||
      indexType ||
      capitolId ||
      subCapitolId ||
      classificationId ||
      predefinedOrderId ||
      registerInitDate ||
      sendStatus ||
      groupInterId ||
      mediaId ||
      roadId ||
      concessionId ||
      trackValue ||
      direction ||
      margin ||
      laneValue ||
      pkInit ||
      pkEnd ||
      description ||
      notes
    ) {
      if (isOffline) {
        // El `id` va DENTRO de `commInput`, así que al reconectar
        // `syncCommunication` replaya SAVE_COMMUNICATION por su camino de
        // UPDATE (no crea nada nuevo) y le pone su propio `clientOpId`
        // derivado de `op.id` → un reintento no duplica. Sin `parentMutation`
        // ni `parentInput`: en una edición el padre ya existe.
        await addCompoundOperation({
          type: 'communication',
          parentOrderId: (parentOrderId as string) ?? '',
          parentOrderRoid: Array.isArray(parentOrder?.roid)
            ? parentOrder.roid.join('.')
            : (parentOrder?.roid ?? ''),
          isCampaignParent: false,
          sessionSnapshots: {},
          localImages: [],
          communicationData: {
            parentMutation: null,
            parentInput: null,
            commInput,
            redirectPath: redirectTarget,
          },
        });
        queuedOffline = true;
      } else {
        // Bloque C-1 + C-7: SAVE_COMMUNICATION (update path) actualiza
        // Communication + Order paralela atómicamente con todos los campos
        // (Tipo B: concession/road/PKs/capitol/classification + Tipo C:
        // description/notes/registerInitDate). Sustituye UPDATE_COMMUNICATION
        // y los UPDATE_ORDER + UPDATE_ORDER_RELATIONSHIPS legacy.
        await client.mutate({
          mutation: SAVE_COMMUNICATION,
          variables: {
            input: {
              // Idempotencia online: UUID estable por operación (RetryLink no duplica).
              clientOpId: crypto.randomUUID(),
              ...commInput,
            },
          },
        });
      }
    }

    // Refetches de caché: antes eran fire-and-forget (rechazos no gestionados)
    // y uno deref-eaba parentExpeditionOrderIDs[0] sin guard. allSettled: un
    // refetch caído NO aborta un guardado ya persistido.
    //
    // Sin red no se piden: son `network-only`, así que fallarían los cuatro y
    // solo servirían para llenar la consola de errores. La caché la refresca
    // `syncCommunication` al reproducir la cola.
    if (!isOffline) await Promise.allSettled([
      client.query({
        fetchPolicy: 'network-only',
        query: GET_ORDER_BY_ID,
        variables: { id: parentOrderId },
      }),
      ...(parentOrder.parentExpeditionOrderIDs?.[0]
        ? [
            client.query({
              fetchPolicy: 'network-only',
              query: GET_ORDER_BY_ID,
              variables: { id: parentOrder.parentExpeditionOrderIDs[0] },
            }),
          ]
        : []),
      ...(storedDataOTSelected?.id
        ? [
            client.query({
              fetchPolicy: 'network-only',
              query: GET_ORDER_BY_ID,
              variables: { id: storedDataOTSelected.id },
            }),
          ]
        : []),
      client.query({
        fetchPolicy: 'network-only',
        query: GET_COMMUNICATION_BY_ID,
        variables: { id: communicationId },
      }),
    ]);
  }

  // Clean up all possible keys (base, OT, and Campaign)
  sessionStorage.removeItem(formNaming.EDIT_COMMUNICATION_FORM);
  sessionStorage.removeItem(formNaming.EDIT_COMMUNICATION_FORM_OT);
  sessionStorage.removeItem(formNaming.EDIT_COMMUNICATION_FORM_CAMPAIGN);
  sessionStorage.removeItem(COMMUNICATION_EDIT_SCOPE.ownerKey);
  // Also clean up navigation context
  sessionStorage.removeItem(formNaming.COMMUNICATION_NAVIGATION_CONTEXT);

  dispatch(deselectedRowModalTable(formNaming.EDIT_COMMUNICATION_FORM));
  dispatch(deselectedRowModalTable(formNaming.EDIT_COMMUNICATION_FORM_OT));
  dispatch(deselectedRowModalTable(formNaming.EDIT_COMMUNICATION_FORM_CAMPAIGN));

  dispatch(indexTabFormReset(tabsType.EDIT_COMMUNICATION));

  // Mismo evento que emiten crear comunicación y el catch de la action: es lo
  // que la UI escucha para avisar de que queda trabajo pendiente de sincronizar.
  if (queuedOffline) {
    window.dispatchEvent(new CustomEvent('offline-communication-queued'));
  }

  return redirect(redirectTarget);
};
